import type { AnnanRole, Perspective } from "@samklang/schemas";
import type { SummaryItem } from "@samklang/conversation-engine";
import type { Kartlaggning, PerspectiveRun } from "@/stores/kartlaggning-store";

export const PERSPECTIVES: readonly Perspective[] = ["elev", "mentor", "vardnadshavare", "annan"] as const;

const ANNAN_ROLE_LABELS: Readonly<Record<AnnanRole, string>> = {
  specialpedagog: "Specialpedagog",
  kurator: "Kurator",
  skolskoterska: "Skolsköterska",
  larare: "Lärare",
  socialpedagog: "Socialpedagog",
  fritidspersonal: "Fritidspersonal",
  annan_roll: "Annan roll",
};

export const ANNAN_ROLES: readonly AnnanRole[] = [
  "specialpedagog",
  "kurator",
  "skolskoterska",
  "larare",
  "socialpedagog",
  "fritidspersonal",
  "annan_roll",
];

/** Labelled perspective, e.g. "Elevens perspektiv" or the concrete role label for "annan". */
export function perspectiveLabel(perspective: Perspective, role?: AnnanRole): string {
  switch (perspective) {
    case "elev":
      return "Elevens perspektiv";
    case "mentor":
      return "Mentorns perspektiv";
    case "vardnadshavare":
      return "Vårdnadshavarens perspektiv";
    case "annan":
      return role ? ANNAN_ROLE_LABELS[role] : "Annan roll";
  }
}

/** Short neutral label used in chips and compact rows, e.g. "Elev", "Mentor", "Vårdnadshavare". */
export function perspectiveShortLabel(perspective: Perspective, role?: AnnanRole): string {
  switch (perspective) {
    case "elev":
      return "Elev";
    case "mentor":
      return "Mentor";
    case "vardnadshavare":
      return "Vårdnadshavare";
    case "annan":
      return role ? ANNAN_ROLE_LABELS[role] : "Annan";
  }
}

/** Maps a URL path segment to a valid perspective, or undefined when invalid. */
export function validatePerspective(value: string): Perspective | undefined {
  return PERSPECTIVES.find((perspective) => perspective === value);
}

export function runStatusLabel(status: PerspectiveRun["status"]): string {
  switch (status) {
    case "planned":
      return "Väntar på svar";
    case "in_progress":
      return "Pågår";
    case "done":
      return "Klar";
  }
}

export function actionStatusLabel(status: "planned" | "in_progress" | "done" | "cancelled"): string {
  switch (status) {
    case "planned":
      return "Planerad";
    case "in_progress":
      return "Pågår";
    case "done":
      return "Genomförd";
    case "cancelled":
      return "Avbruten";
  }
}

export function formatDate(iso: string | undefined): string {
  if (!iso) return "—";
  const value = new Date(iso);
  if (Number.isNaN(value.getTime())) return "—";
  return new Intl.DateTimeFormat("sv-SE", { dateStyle: "medium" }).format(value);
}

export function formatDateTime(iso: string | undefined): string {
  if (!iso) return "—";
  const value = new Date(iso);
  if (Number.isNaN(value.getTime())) return "—";
  return new Intl.DateTimeFormat("sv-SE", { dateStyle: "medium", timeStyle: "short" }).format(value);
}

export interface RunWithSummary {
  readonly run: PerspectiveRun;
  readonly perspective: Perspective;
  readonly summary: readonly SummaryItem[];
}

/** All completed runs that carry a summary, together with their perspective. */
export function completedRuns(kartlaggning: Kartlaggning): readonly RunWithSummary[] {
  return kartlaggning.runs.flatMap((run) => {
    if (run.status !== "done" || !run.summary || run.summary.length === 0) return [];
    return [{ run, perspective: run.perspective, summary: run.summary }];
  });
}

export interface AnswerView {
  readonly perspective: Perspective;
  readonly role?: AnnanRole;
  readonly item: SummaryItem;
  readonly run: PerspectiveRun;
}

export interface QuestionGroup {
  readonly questionId: string;
  readonly question: string;
  readonly theme: string;
  readonly answers: readonly AnswerView[];
}

/** Groups anonymised summary items across perspectives by question for side-by-side comparison. */
export function groupByQuestion(kartlaggning: Kartlaggning): readonly QuestionGroup[] {
  const groups = new Map<string, { readonly questionId: string; readonly question: string; readonly theme: string; answers: AnswerView[] }>();
  for (const { summary, run, perspective } of completedRuns(kartlaggning)) {
    for (const item of summary) {
      const existing = groups.get(item.questionId);
      const view: AnswerView = {
        perspective,
        ...(run.role !== undefined ? { role: run.role } : {}),
        item,
        run,
      };
      if (existing) {
        existing.answers.push(view);
      } else {
        groups.set(item.questionId, {
          questionId: item.questionId,
          question: item.question,
          theme: item.theme,
          answers: [view],
        });
      }
    }
  }
  return [...groups.values()].map(({ answers, ...rest }) => ({ ...rest, answers }));
}

export interface MetricPoint {
  readonly kartlaggningId: string;
  readonly date: string;
  readonly participantReference: string;
  readonly perspective: Perspective;
  readonly role?: AnnanRole;
  readonly metricKey: string;
  readonly metricLabel: string;
  readonly value: number;
}

/** All metric (scale-with-metricKey) values across every kartläggning for one participant. */
export function metricPoints(participantReference: string, kartlaggningar: readonly Kartlaggning[]): readonly MetricPoint[] {
  const points: MetricPoint[] = [];
  for (const kartlaggning of kartlaggningar) {
    if (kartlaggning.participantReference !== participantReference) continue;
    for (const run of kartlaggning.runs) {
      if (run.status !== "done" || !run.summary) continue;
      for (const item of run.summary) {
        if (item.metricKey === undefined || item.metricValue === undefined) continue;
        points.push({
          kartlaggningId: kartlaggning.id,
          date: kartlaggning.createdAt,
          participantReference: kartlaggning.participantReference,
          perspective: run.perspective,
          ...(run.role !== undefined ? { role: run.role } : {}),
          metricKey: item.metricKey,
          metricLabel: item.metricLabel ?? item.metricKey,
          value: item.metricValue,
        });
      }
    }
  }
  return points;
}

/** A scale wording outcome shared by the PDF/compilation renderers. */
export function metricOutcome(group: QuestionGroup): { readonly label: string; readonly perPerspective: readonly { label: string; value: number }[] } | undefined {
  const numeric = group.answers.filter((answer) => answer.item.metricValue !== undefined);
  if (numeric.length === 0) return undefined;
  return {
    label: group.answers[0]?.item.metricLabel ?? group.question,
    perPerspective: numeric.map((answer) => ({
      label: perspectiveShortLabel(answer.perspective, answer.role),
      value: answer.item.metricValue as number,
    })),
  };
}

const CATEGORY_BLOCK: Readonly<Record<string, string>> = {
  works: "Vad fungerar",
  gives_energy: "Styrkor",
  hard: "Vad är svårt",
  takes_energy: "Vad tar energi",
  change: "Behov och förändringar",
  important: "Viktigt för eleven",
  understand: "Viktigt att ta med",
  other: "Övriga iakttagelser",
};

export function categoryBlockLabel(category: string): string {
  return CATEGORY_BLOCK[category] ?? "Övriga iakttagelser";
}

export interface UskBlock {
  readonly heading: string;
  readonly items: readonly { readonly question: string; readonly answer: string }[];
}

export interface UskPerspectiveSection {
  readonly heading: string;
  readonly source: string;
  readonly blocks: readonly UskBlock[];
}

/** Builds a per-perspective structured USK underlag from all completed runs. */
export function buildUskPerspectiveSections(kartlaggning: Kartlaggning): readonly UskPerspectiveSection[] {
  return completedRuns(kartlaggning).map(({ run, perspective, summary }) => {
    const byCategory = new Map<string, { readonly question: string; readonly answer: string }[]>();
    for (const item of summary) {
      const key = CATEGORY_BLOCK[item.category] ?? "Övriga iakttagelser";
      const prior = byCategory.get(key) ?? [];
      byCategory.set(key, [...prior, { question: item.question, answer: item.answer }]);
    }
    return {
      heading: perspectiveLabel(perspective, run.role),
      source: sourceLine(run, perspective),
      blocks: [...byCategory.entries()].map(([heading, items]) => ({ heading, items })),
    };
  });
}

export function sourceLine(run: PerspectiveRun, perspective: Perspective): string {
  return [
    `Perspektiv: ${perspectiveLabel(perspective, run.role)}`,
    run.conductedWith ? `Genomfört tillsammans med: ${run.conductedWith}` : null,
    `Registrerad av: ${run.registeredBy || "—"}`,
    `Datum: ${formatDate(run.finishedAt ?? run.startedAt)}`,
  ]
    .filter((part): part is string => Boolean(part))
    .join(" · ");
}

export interface ComparisonCell {
  readonly perspective: Perspective;
  readonly role?: AnnanRole;
  readonly label: string;
  readonly answer: string;
  readonly metricValue?: number;
}

export interface ComparisonRow {
  readonly questionId: string;
  readonly question: string;
  readonly theme: string;
  readonly cells: readonly ComparisonCell[];
  readonly agreement: boolean;
}

/** Side-by-side comparison of a question across the perspectives that answered it. */
export function comparisonRows(kartlaggning: Kartlaggning): readonly ComparisonRow[] {
  return groupByQuestion(kartlaggning)
    .map((group) => {
      const cells: ComparisonCell[] = group.answers.map((answer) => ({
        perspective: answer.perspective,
        ...(answer.role !== undefined ? { role: answer.role } : {}),
        label: perspectiveShortLabel(answer.perspective, answer.role),
        answer: answer.item.answer,
        ...(answer.item.metricValue !== undefined ? { metricValue: answer.item.metricValue } : {}),
      }));
      const numeric = cells.filter((cell) => cell.metricValue !== undefined);
      let agreement = true;
      if (numeric.length >= 2) {
        const firstMetric = numeric[0]?.metricValue;
        agreement = numeric.every((cell) => cell.metricValue === firstMetric);
      } else if (cells.length >= 2) {
        const first = cells[0]?.answer;
        agreement = cells.every((cell) => cell.answer === first);
      }
      return { questionId: group.questionId, question: group.question, theme: group.theme, cells, agreement };
    })
    .filter((row) => row.cells.length > 0);
}

export interface SamladBild {
  readonly agreementRows: readonly ComparisonRow[];
  readonly differenceRows: readonly ComparisonRow[];
  readonly strengths: readonly { readonly perspective: Perspective; readonly role?: AnnanRole; readonly label: string; readonly item: SummaryItem }[];
  readonly difficulties: readonly { readonly perspective: Perspective; readonly role?: AnnanRole; readonly label: string; readonly item: SummaryItem }[];
  readonly needs: readonly { readonly perspective: Perspective; readonly role?: AnnanRole; readonly label: string; readonly item: SummaryItem }[];
}

/** Cross-perspective aggregated image: agreements, differences, strengths, difficulties and needs. */
export function buildSamladBild(kartlaggning: Kartlaggning): SamladBild {
  const rows = comparisonRows(kartlaggning);
  const agreementRows = rows.filter((row) => row.agreement);
  const differenceRows = rows.filter((row) => !row.agreement);
  type CollectEntry = { readonly perspective: Perspective; readonly role?: AnnanRole; readonly label: string; readonly item: SummaryItem };
  const collect = (categories: readonly string[]): readonly CollectEntry[] => {
    const result: CollectEntry[] = [];
    for (const { summary, run, perspective } of completedRuns(kartlaggning)) {
      for (const item of summary) {
        if (categories.includes(item.category)) {
          result.push({
            perspective,
            ...(run.role !== undefined ? { role: run.role } : {}),
            label: perspectiveLabel(perspective, run.role),
            item,
          });
        }
      }
    }
    return result;
  };
  return {
    agreementRows,
    differenceRows,
    strengths: collect(["works", "gives_energy"]),
    difficulties: collect(["hard", "takes_energy"]),
    needs: collect(["change"]),
  };
}

/** Neutral phrasing for a differing scale question, never judging which perspective is correct. */
export function neutrallyDescribeDifference(difference: ComparisonRow): string {
  const scale = difference.cells.filter((cell) => cell.metricValue !== undefined);
  if (scale.length >= 2) {
    const sorted = [...scale].sort((a, b) => (a.metricValue as number) - (b.metricValue as number));
    const lowest = sorted[0];
    const highest = sorted[sorted.length - 1];
    if (lowest && highest && lowest.metricValue !== highest.metricValue) {
      return `${lowest.label} skattar det som svårare (${lowest.metricValue}/5) än ${highest.label} (${highest.metricValue}/5).`;
    }
  }
  return `${difference.cells.map((cell) => `${cell.label}: ${cell.answer}`).join(" · ")}.`;
}
